import { useCallback, useEffect, useRef, useState } from 'react';

import type { ChangedResponse } from 'bundles/compound-assessments/components/local-state/changed-response/types';
import type {
  BatchRubricUpdateFunction,
  SubmitGradesFunction,
} from 'bundles/grading/api/BatchUpdateAndSubmitGradesMutationWrapper';
import type { GradingStepActionMetadata } from 'bundles/grading/api/types';
import type { SgaPageData } from 'bundles/grading/components/staff-graded-assignments/SGAPageContext';
import { AutoSaveTask } from 'bundles/grading/components/staff-graded-assignments/auto-save/AutoSaveTask';
import type {
  AutoSaveTaskState,
  ReviewPartProgress,
} from 'bundles/grading/components/staff-graded-assignments/auto-save/utils';

export const useAutoSaveTasksManager = (
  batchRubricUpdate: BatchRubricUpdateFunction,
  submitGrades: SubmitGradesFunction
) => {
  // Keep a map of AutoSaveTask behaviors as {[submitterId]: AutoSaveTask} object in `autoSaveTasks` ref.
  const autoSaveTasks = useRef<Record<string, AutoSaveTask>>({});

  // Keep a map of states for each AutoSaveTask as {[submitterId]: AutoSaveTaskState} in `tracker` state.
  const [tracker, setTracker] = useState<Record<string, AutoSaveTaskState>>({});

  // Aggregated values of all `AutoSaveTaskState`s
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // recalculate aggregated values on AutoSaveTask's status change
  useEffect(() => {
    let _hasUnsavedChanges = false;
    let _isSaving = false;
    let _isSubmitting = false;

    Object.values(tracker).forEach((item) => {
      if (!_hasUnsavedChanges && item.hasUnsavedChanges) {
        _hasUnsavedChanges = true;
      }
      if (!_isSaving && item.isSaving) {
        _isSaving = true;
      }
      if (!_isSubmitting && item.isSubmitting) {
        _isSubmitting = true;
      }
    });
    setHasUnsavedChanges(_hasUnsavedChanges);
    setIsSaving(_isSaving);
    setIsSubmitting(_isSubmitting);
  }, [tracker]);

  // Register an instance of `AutoSaveTask` and a copy of its state in `AutoSaveTaskState`
  const register = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (submitterId: string, pageData: SgaPageData, gradingStepActionMetadata: GradingStepActionMetadata | null) => {
      if (
        submitterId &&
        gradingStepActionMetadata &&
        gradingStepActionMetadata.allowedGradingActions &&
        gradingStepActionMetadata.gradingTaskIdHash
      ) {
        // create new AutoSaveTask and Tracker for submitterId when it is opened first time
        if (!autoSaveTasks.current[submitterId]) {
          autoSaveTasks.current[submitterId] = new AutoSaveTask({
            submissionReference: {
              groupId: pageData.gradingGroupId || pageData.currentContextId, // ensure we use sub-groupIds, like gradingGroupId, if it's available, as preference over contextId
              itemId: pageData.currentItemId,
              submitterId,
            },
            allowedGradingActions: gradingStepActionMetadata.allowedGradingActions,
            conflictMetadata: gradingStepActionMetadata.conflictMetadata,
            gradingTaskIdHash: gradingStepActionMetadata.gradingTaskIdHash,
          });
          setTracker((prevValue) => {
            return { ...prevValue, [submitterId]: { ...autoSaveTasks.current[submitterId].getState() } };
          });
        }
      }
    },
    []
  );

  const setData = useCallback<(...args: $TSFixMe[]) => $TSFixMe>((submitterId: string, value: ChangedResponse[]) => {
    autoSaveTasks.current[submitterId].setLatestDataInCache(value);
    setTracker((prevValue) => {
      return {
        ...prevValue,
        [submitterId]: {
          ...prevValue[submitterId],
          ...autoSaveTasks.current[submitterId].getState(),
        },
      };
    });
  }, []);

  const getData = useCallback<(...args: $TSFixMe[]) => $TSFixMe>((submitterId: string): ChangedResponse[] => {
    return autoSaveTasks.current[submitterId]?.getLatestDataInCache() || [];
  }, []);

  // Calls `debouncedSave` on `autoSaveTask`
  const save = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (submitterId: string, updatedReviewPartProgress: ReviewPartProgress, flush?: boolean) => {
      // The `debouncedAutosave` takes `onStateChange` callback, which updates relevant `tracker`
      // upon state update.
      const onStateChange = () => {
        setTracker((prevValue) => {
          return {
            ...prevValue,
            [submitterId]: {
              ...prevValue[submitterId],
              ...autoSaveTasks.current[submitterId].getState(),
            },
          };
        });
      };
      autoSaveTasks.current[submitterId].debouncedSave(
        batchRubricUpdate,
        submitGrades,
        onStateChange,
        updatedReviewPartProgress
      );
      // skip debounced delay if we force SubmitToGradebook
      if (flush) {
        autoSaveTasks.current[submitterId].debouncedSave.flush();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [batchRubricUpdate, submitGrades]
  );

  return { tracker, hasUnsavedChanges, isSaving, isSubmitting, register, setData, getData, save };
};
